import React from 'react';
import { Tooltip } from '@mui/material';
import { FontSize, FontWeight } from 'lib/styles';
import styled from 'styled-components';
import { Space } from 'styles/themeSpaces';
import { Text, Main, Background, Border } from 'styles/themeColors';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import { Edit, Info, InfoOutlined, InfoRounded } from '@mui/icons-material';
import { Height } from 'styles/themeHeights';
import { SALES_ORDER_TYPES, SALES_ORDER_LABELS, SalesOrderType } from 'constants/salesOrderTypes';

const Heading = styled.h3`
  font-size: ${FontSize.medium};
  line-height: 28px;
  margin-bottom: 12px;
  font-weight: ${FontWeight.bold};
`;

const ColumnName = styled.div<any>`
  min-width: 120px;
  font-size: ${Space.sp2x};
  line-height: 20px;
  color: ${({ $color }) => ($color ? $color : Text.primary)};
  margin-right: ${Space.sp3x};
`;

const ColumnValue = styled.div<any>`
  font-size: ${({ $fontSize }) => ($fontSize ? $fontSize : '16px')};
  line-height: ${({ $lineHeight }) => ($lineHeight ? $lineHeight : '20px')};
  color: ${({ $color }) => ($color ? $color : Text.primary)};
  background-color: ${({ $bgcolor }) => ($bgcolor ? $bgcolor : 'transparent')};
  padding: ${({ $padding }) => ($padding ? $padding : '0px')};
  font-weight: ${({ $weight }) => ($weight ? $weight : '400')};
  border-radius: 6px;
`;

const HyperlinkButton = styled.p`
  text-decoration: underline;
  font-size: 16px;
  color: ${Main.brandDark};
  display: inline-block;

  &:hover {
    cursor: pointer;
  }
`;
interface Props {
  salesOrderId?: string;
  deliveryStatus?: string;
  orderRejectionStatus?: string;
  buttonClick: (value: boolean) => void;
  openSalesOrderDetails: (value: boolean) => void;
  isTimeout?: boolean;
  isAdmin?: boolean;
  orderType: SalesOrderType;
  salesOrderInProgress?: boolean;
}
const SalesOrder = ({
  salesOrderId,
  deliveryStatus,
  orderRejectionStatus,
  buttonClick,
  openSalesOrderDetails,
  isTimeout,
  isAdmin,
  orderType,
  salesOrderInProgress
}: Props) => {
  return (
    <FlexboxContainerComponent
      flexDirection="column"
      padding="16px"
      width="100%"
      margin='0px 0px 24px 0px'
    >
      <FlexboxContainerComponent>
        <Heading>{SALES_ORDER_LABELS.HEADING}</Heading>
      </FlexboxContainerComponent>
      <FlexboxContainerComponent alignItems="center" flexDirection="row">
        <ColumnName>{SALES_ORDER_LABELS.SALES_ORDER_NUMBER_LABEL}</ColumnName>
        <ColumnValue $padding="0px 24px 0px 0px">{salesOrderInProgress ? SALES_ORDER_LABELS.PROCESSING : salesOrderId ? salesOrderId : 'N/A'}</ColumnValue>
        {isAdmin && orderType === SALES_ORDER_TYPES.MANUAL ? (
          salesOrderId ? (
            <Button
              size="s"
              leftIcon={(size: string) => (
                <Edit
                  style={{ height: Height.s, width: '12px', color: Text.secondary }}
                />
              )}
              color={Text.secondary}
              bgColor="transparent"
              borderColor={Border.main}
              onClick={() => {
                buttonClick(true);
              }}
              disabled={salesOrderInProgress}
            >
              {SALES_ORDER_LABELS.EDIT_BUTTON}
            </Button>
          ) : (
            <Button
              size="s"
              leftIcon={(size: string) => (
                <Edit
                  style={{ height: '12px', width: '12px', color: 'white' }}
                />
              )}
              onClick={() => {
                buttonClick(true);
              }}
              disabled={salesOrderInProgress}
            >
              {SALES_ORDER_LABELS.ENTER_BUTTON}
            </Button>
          )
        ) : null}
      </FlexboxContainerComponent>
      {orderType && (
        <FlexboxContainerComponent alignItems="center" flexDirection="row" margin="6px 0px">
          <ColumnName>{SALES_ORDER_LABELS.ORDER_TYPE_LABEL}</ColumnName>
          <ColumnValue $padding="0px 8px 0px 0px">
            {salesOrderInProgress ? SALES_ORDER_LABELS.PROCESSING :
              orderType === SALES_ORDER_TYPES.API ? SALES_ORDER_LABELS.API_SALES_ORDER_TEXT :
                SALES_ORDER_LABELS.MANUAL_SALES_ORDER_TEXT}
          </ColumnValue>
          <Tooltip
            title={salesOrderInProgress ? SALES_ORDER_LABELS.SALES_ORDER_IN_PROGRESS :
              orderType === SALES_ORDER_TYPES.API ? SALES_ORDER_LABELS.API_TOOLTIP :
                SALES_ORDER_LABELS.MANUAL_TOOLTIP}
            placement="right"
            arrow
          >
            <InfoOutlined style={{ height: '24px', width: '24px', color: Text.primary, cursor: 'pointer' }} />
          </Tooltip>
        </FlexboxContainerComponent>
      )}
      {salesOrderId && !isTimeout ? (
        orderRejectionStatus && deliveryStatus ? (
          orderRejectionStatus != '' && deliveryStatus != '' ? (
            <FlexboxContainerComponent flexDirection="column">
              <FlexboxContainerComponent
                margin="6px 0px"
                alignItems="center"
                flexDirection="row"
              >
                <ColumnName>{SALES_ORDER_LABELS.REJECTION_STATUS_LABEL}</ColumnName>
                <ColumnValue $color="white" $padding="4px" $bgcolor={Background.navy} $fontSize='12px' $weight='700' $lineHeight='12px'>
                  {orderRejectionStatus.toString().toUpperCase()}
                </ColumnValue>
              </FlexboxContainerComponent>
              <FlexboxContainerComponent
                alignItems="center"
                flexDirection="row"
              >
                <ColumnName>{SALES_ORDER_LABELS.DELIVERY_STATUS_LABEL}</ColumnName>
                <ColumnValue $color="white" $padding="4px" $bgcolor={Background.navy} $fontSize='12px' $weight='700' $lineHeight='12px'>
                  {deliveryStatus.toString().toUpperCase()}
                </ColumnValue>
              </FlexboxContainerComponent>
              <FlexboxContainerComponent>
                <HyperlinkButton
                  onClick={() => {
                    openSalesOrderDetails(true);
                  }}
                >
                  {SALES_ORDER_LABELS.SHOW_ALL_BUTTON}
                </HyperlinkButton>
              </FlexboxContainerComponent>
            </FlexboxContainerComponent>
          ) : (
            <FlexboxContainerComponent flexDirection="column">
              <FlexboxContainerComponent
                margin="6px 0px"
                alignItems="center"
                flexDirection="row"
              >
                <ColumnName>{SALES_ORDER_LABELS.REJECTION_STATUS_LABEL}</ColumnName>
                <ColumnValue $color={Text.placeholder} $padding="4px" $bgcolor={Background.shaded}>
                  {SALES_ORDER_LABELS.NOT_AVAILABLE}
                </ColumnValue>
              </FlexboxContainerComponent>
              <FlexboxContainerComponent
                alignItems="center"
                flexDirection="row"
              >
                <ColumnName>{SALES_ORDER_LABELS.DELIVERY_STATUS_LABEL}</ColumnName>
                <ColumnValue $color={Text.placeholder} $padding="4px" $bgcolor={Background.shaded}>
                  {SALES_ORDER_LABELS.NOT_AVAILABLE}
                </ColumnValue>
              </FlexboxContainerComponent>
              <FlexboxContainerComponent>
                <HyperlinkButton
                  onClick={() => {
                    openSalesOrderDetails(true);
                  }}
                >
                  {SALES_ORDER_LABELS.SHOW_ALL_BUTTON}
                </HyperlinkButton>
              </FlexboxContainerComponent>
            </FlexboxContainerComponent>
          )
        ) : (
          <FlexboxContainerComponent
            padding="18px 0px"
            alignItems="center"
            flexDirection="row"
          >
            <ColumnValue
              $color={Text.placeholder}
              $weight="700"
              $padding="4px"
              $bgcolor={Background.shaded}
            >
              {SALES_ORDER_LABELS.SO_NOT_EXIST}
            </ColumnValue>
          </FlexboxContainerComponent>
        )
      ) : isTimeout && salesOrderId ? (
        <FlexboxContainerComponent
          padding="18px 0px"
          alignItems="center"
          flexDirection="row"
        >
          <ColumnValue $weight="700" $padding="4px" $bgcolor={Main.warning}>
            {SALES_ORDER_LABELS.INTEGRATION_NO_RESPONSE}
          </ColumnValue>
        </FlexboxContainerComponent>
      ) : (
        <></>
      )}
    </FlexboxContainerComponent>
  );
};

export default SalesOrder;
